import React, { useState, useEffect, useMemo } from 'react';
import { MdArrowBack, MdSearch, MdFilterList, MdSort, MdRefresh, MdAutoFixHigh, MdRateReview } from 'react-icons/md';
import {
  getAssignmentSubmissions,
  getGradingStats,
  getRubricsByAssignment,
  autoGradeSubmission,
  getRegradeRequests,
  updateRegradeStatus,
  batchFinalizeGrades
} from '../../utils/api/AssignmentsApi';

const StatusBadge = ({ status }) => {
  const styles = {
    draft: 'bg-gray-100 text-gray-600',
    submitted: 'bg-blue-100 text-blue-700',
    grading: 'bg-yellow-100 text-yellow-700',
    graded: 'bg-green-100 text-green-700',
    returned: 'bg-purple-100 text-purple-700'
  };

  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${styles[status] || styles.draft}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const GradingDashboard = ({ assignment, classContext, onBack, onGradeSubmission }) => {
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [rubrics, setRubrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name'); // name, submitted_at, score, status
  const [sortDir, setSortDir] = useState('asc');
  const [autoGrading, setAutoGrading] = useState(null); // submission_id being auto-graded
  const [activeTab, setActiveTab] = useState('submissions'); // 'submissions' | 'regrades'
  const [regradeRequests, setRegradeRequests] = useState([]);
  const [regradeLoading, setRegradeLoading] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState({});
  const [isReleasing, setIsReleasing] = useState(false);

  const components = assignment.components || [];

  useEffect(() => {
    loadData();
  }, [assignment.assignment_id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [subsResult, statsResult, rubricsResult] = await Promise.all([
        getAssignmentSubmissions(assignment.assignment_id, null, classContext, { groupByStudent: true }),
        getGradingStats(assignment.assignment_id, classContext),
        getRubricsByAssignment(assignment.assignment_id, classContext)
      ]);

      setSubmissions(subsResult.submissions || []);
      setStats(statsResult);
      setRubrics(rubricsResult.rubrics || []);

      // Load regrade requests
      try {
        const regradeResult = await getRegradeRequests({ assignment_id: assignment.assignment_id }, classContext);
        setRegradeRequests(regradeResult.regrade_requests || []);
      } catch (regradeErr) {
        console.error('Error loading regrade requests:', regradeErr);
      }
    } catch (err) {
      console.error('Error loading grading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoGrade = async (submissionId) => {
    try {
      setAutoGrading(submissionId);
      await autoGradeSubmission(submissionId, classContext);
      await loadData(); // Refresh
    } catch (err) {
      console.error('Auto-grade failed:', err);
      setError(err.message);
    } finally {
      setAutoGrading(null);
    }
  };

  const handleAutoGradeAll = async () => {
    const eligible = submissions.filter(s => s.status === 'submitted');
    if (eligible.length === 0) return;
    if (!window.confirm(`Auto-grade ${eligible.length} submission(s)? This will score linked MCQ questions automatically.`)) return;

    for (const sub of eligible) {
      try {
        setAutoGrading(sub.submission_id);
        await autoGradeSubmission(sub.submission_id, classContext);
      } catch (err) {
        console.error(`Auto-grade failed for ${sub.student_id}:`, err);
      }
    }
    setAutoGrading(null);
    await loadData();
  };

  const handleRegradeAction = async (requestId, status) => {
    try {
      setRegradeLoading(true);
      await updateRegradeStatus(requestId, {
        status,
        resolution_notes: resolutionNotes[requestId] || ''
      }, classContext);
      // Refresh regrades
      const regradeResult = await getRegradeRequests({ assignment_id: assignment.assignment_id }, classContext);
      setRegradeRequests(regradeResult.regrade_requests || []);
    } catch (err) {
      console.error('Error updating regrade:', err);
      setError(err.message);
    } finally {
      setRegradeLoading(false);
    }
  };

  const handleReleaseAllGrades = async () => {
    const gradedCount = submissions.filter(s => s.status === 'graded').length;
    if (gradedCount === 0) {
      alert('No graded submissions to release.');
      return;
    }
    if (!window.confirm(`Release grades for ${gradedCount} graded submission(s)? Students will be able to see their grades and feedback.`)) return;

    try {
      setIsReleasing(true);
      const result = await batchFinalizeGrades(assignment.assignment_id, classContext);
      alert(`Released grades for ${result.released_count} submission(s).${result.errors?.length ? ` Errors: ${result.errors.join(', ')}` : ''}`);
      await loadData();
    } catch (err) {
      console.error('Error releasing grades:', err);
      setError(err.message);
    } finally {
      setIsReleasing(false);
    }
  };

  const pendingRegradeCount = regradeRequests.filter(r => r.status === 'pending').length;

  // Filter & sort
  const filteredSubmissions = useMemo(() => {
    let result = [...submissions];

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s =>
        (s.student_id || '').toLowerCase().includes(q) ||
        (s.student_email || '').toLowerCase().includes(q)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(s => s.status === statusFilter);
    }

    // Sort
    result.sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'name':
          cmp = (a.student_id || '').localeCompare(b.student_id || '');
          break;
        case 'submitted_at':
          cmp = new Date(a.submitted_at || 0) - new Date(b.submitted_at || 0);
          break;
        case 'score':
          cmp = (a.grade || 0) - (b.grade || 0);
          break;
        case 'status': {
          const order = { draft: 0, submitted: 1, grading: 2, graded: 3, returned: 4 };
          cmp = (order[a.status] || 0) - (order[b.status] || 0);
          break;
        }
        default:
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [submissions, searchQuery, statusFilter, sortBy, sortDir]);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const hasLinkedQuestions = rubrics.some(r =>
    (r.items || []).some(item => (item.linked_questions || []).length > 0)
  );

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
          >
            <MdArrowBack className="text-xl" />
          </button>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900 m-0">{assignment.title}</h2>
            <p className="text-sm text-gray-500 m-0 mt-0.5">Grade Submissions</p>
          </div>
          <div className="flex items-center gap-2">
            {hasLinkedQuestions && (
              <button
                onClick={handleAutoGradeAll}
                disabled={autoGrading !== null}
                className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                <MdAutoFixHigh className="text-base" />
                Auto-Grade All
              </button>
            )}
            <button
              onClick={handleReleaseAllGrades}
              disabled={isReleasing}
              className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
            >
              {isReleasing ? 'Releasing...' : 'Release All Grades'}
            </button>
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <MdRefresh className={`text-base ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
          <div className="flex gap-6">
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">{stats.total_submissions}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">{stats.submitted}</div>
              <div className="text-xs text-gray-500">Submitted</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-yellow-600">{stats.pending_grading}</div>
              <div className="text-xs text-gray-500">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">{stats.graded}</div>
              <div className="text-xs text-gray-500">Graded</div>
            </div>
            {components.length > 0 && (
              <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
                <span>Components:</span>
                {components.map((c, i) => {
                  const color = c.type === 'document' ? 'bg-blue-100 text-blue-700' :
                    c.type === 'erd' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700';
                  return (
                    <span key={i} className={`px-2 py-0.5 rounded text-xs font-semibold ${color}`}>
                      {c.title} ({c.points}pts)
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-6 flex-shrink-0">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('submissions')}
            className={`py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'submissions'
                ? 'border-blue-500 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Submissions
          </button>
          <button
            onClick={() => setActiveTab('regrades')}
            className={`py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'regrades'
                ? 'border-blue-500 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <MdRateReview className="text-base" />
            Regrade Requests
            {pendingRegradeCount > 0 && (
              <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] text-center">
                {pendingRegradeCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {activeTab === 'submissions' && (
      <>
      {/* Filters */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search students..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <MdFilterList className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="submitted">Submitted</option>
              <option value="grading">Grading</option>
              <option value="graded">Graded</option>
              <option value="returned">Returned</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-6 mt-3 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 text-red-800 text-sm">
          {error}
        </div>
      )}

      {/* Submissions Table */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center h-32 text-gray-500">Loading submissions...</div>
        ) : filteredSubmissions.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-400">
            {submissions.length === 0 ? 'No submissions yet' : 'No matching submissions'}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:text-gray-900"
                    onClick={() => toggleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Student
                      {sortBy === 'name' && <MdSort className={sortDir === 'desc' ? 'rotate-180' : ''} />}
                    </div>
                  </th>
                  <th
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:text-gray-900"
                    onClick={() => toggleSort('status')}
                  >
                    <div className="flex items-center gap-1">
                      Status
                      {sortBy === 'status' && <MdSort className={sortDir === 'desc' ? 'rotate-180' : ''} />}
                    </div>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Attempts
                  </th>
                  <th
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:text-gray-900"
                    onClick={() => toggleSort('submitted_at')}
                  >
                    <div className="flex items-center gap-1">
                      Submitted
                      {sortBy === 'submitted_at' && <MdSort className={sortDir === 'desc' ? 'rotate-180' : ''} />}
                    </div>
                  </th>
                  <th
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:text-gray-900"
                    onClick={() => toggleSort('score')}
                  >
                    <div className="flex items-center gap-1">
                      Score
                      {sortBy === 'score' && <MdSort className={sortDir === 'desc' ? 'rotate-180' : ''} />}
                    </div>
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredSubmissions.map((sub) => (
                  <tr
                    key={sub.submission_id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => onGradeSubmission(sub, filteredSubmissions)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-sm text-gray-900">{sub.student_id}</div>
                      {sub.student_email && (
                        <div className="text-xs text-gray-500">{sub.student_email}</div>
                      )}
                      {sub.is_late && (
                        <span className="text-xs text-orange-600 font-medium">Late</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={sub.status} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {sub.total_attempts || sub.attempt_number || 1}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDate(sub.submitted_at)}
                    </td>
                    <td className="px-4 py-3">
                      {sub.grade !== null && sub.grade !== undefined ? (
                        <span className="text-sm font-semibold text-gray-900">
                          {sub.grade}/{assignment.total_points}
                          {sub.score_percentage !== null && (
                            <span className="text-xs text-gray-500 ml-1">({sub.score_percentage}%)</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2 justify-end">
                        {hasLinkedQuestions && sub.status === 'submitted' && (
                          <button
                            onClick={() => handleAutoGrade(sub.submission_id)}
                            disabled={autoGrading === sub.submission_id}
                            className="px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 disabled:opacity-50"
                          >
                            {autoGrading === sub.submission_id ? 'Grading...' : 'Auto-Grade'}
                          </button>
                        )}
                        <button
                          onClick={() => onGradeSubmission(sub, filteredSubmissions)}
                          className="px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                        >
                          {sub.status === 'graded' || sub.status === 'returned' ? 'Review' : 'Grade'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}

      {/* Regrade Requests Tab */}
      {activeTab === 'regrades' && (
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          {regradeRequests.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-gray-400">
              No regrade requests
            </div>
          ) : (
            <div className="space-y-4">
              {regradeRequests.map(req => (
                <div key={req.request_id || req._id} className="bg-white rounded-lg border border-gray-200 p-5">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm text-gray-900">{req.student_id}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          req.status === 'approved' ? 'bg-green-100 text-green-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {req.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        Requested {req.created_at ? new Date(req.created_at).toLocaleString() : 'Unknown'}
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 mb-3">
                    <p className="text-sm text-gray-700 m-0"><strong>Reason:</strong> {req.reason}</p>
                  </div>

                  {req.status === 'pending' && (
                    <div className="space-y-3">
                      <textarea
                        value={resolutionNotes[req.request_id || req._id] || ''}
                        onChange={(e) => setResolutionNotes(prev => ({
                          ...prev,
                          [req.request_id || req._id]: e.target.value
                        }))}
                        placeholder="Resolution notes (optional)..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                        rows={2}
                      />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleRegradeAction(req.request_id || req._id, 'approved')}
                          disabled={regradeLoading}
                          className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleRegradeAction(req.request_id || req._id, 'denied')}
                          disabled={regradeLoading}
                          className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                        >
                          Deny
                        </button>
                      </div>
                    </div>
                  )}

                  {req.resolution_notes && req.status !== 'pending' && (
                    <div className="mt-2 text-sm text-gray-600">
                      <strong>Resolution:</strong> {req.resolution_notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GradingDashboard;
